import { Fragment, useEffect, useReducer } from 'react'
import { useQuery } from '@tanstack/react-query'
import { onboardingReducer, initialOnboardingState } from './state'
import type { OnboardingState } from './state'
import { WizardMachine } from '@/components/WizardParts'
import { StepRail, Step, STEP_BAR_STYLE, STEP_BAR_CLASSNAME } from '@/components/StepRail'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { ObWz1 } from '@/screens/ob-wz-1/ObWz1'
import { ObWz2 } from '@/screens/ob-wz-2/ObWz2'
import { ObWz3 } from '@/screens/ob-wz-3/ObWz3'
import { ObWz4 } from '@/screens/ob-wz-4/ObWz4'
import { ObWz6 } from '@/screens/ob-wz-6/ObWz6'
import { OB_WZ_1_COPY, OB_WZ_2_COPY, OB_WZ_3_COPY, OB_WZ_4_COPY, OB_WZ_6_COPY } from '@/copy/onboarding'
import type { components } from '@/api/generated/schema'

type Workspace = components['schemas']['Workspace']
type JobRef = components['schemas']['JobRef']

// `ob-wz-5` (repository scope) is a real screen and a real stage in `state.ts`'s own union, but is
// not one of this rail's five steps — see `state.ts`'s own doc comment for why it is skipped.
const STEP_LABELS = [OB_WZ_1_COPY.stepLabel, OB_WZ_2_COPY.stepLabel, OB_WZ_3_COPY.stepLabel, OB_WZ_4_COPY.stepLabel, OB_WZ_6_COPY.stepLabel]

// `state.stage` skips the literal `5` (see `state.ts`), but the rail above is a dense 1..5 — this
// is the one place that gap is bridged, rather than leaving a permanent hole in the rail's own
// ordinal at position 5. Keyed on the exact closed set of non-`'done'` stages (never a plain
// `Record<number, number>`) so indexing it can never produce `undefined`.
const STAGE_ORDINAL: Record<Exclude<OnboardingState['stage'], 'done'>, number> = { 1: 1, 2: 2, 3: 3, 4: 4, 6: 5 }

/**
 * SPA.2 / `a-onboard` — the workspace-connection wizard, design/12-onboarding-and-credentials.md
 * §12.4–§12.6 (stages 2–4). Entered from the dashboard, never from the auth layer (§3.1i rule 8)
 * — `orgId` is a required prop because there is no shell yet to supply "the current organisation"
 * from; whatever mounts this once the shell exists passes it down.
 *
 * **Resumability (§12.6.5 — "derived from what already exists, never a stored step") is now
 * implemented for the one caller that needs it** (`ShellLayer`'s `/onboarding/:workspaceId`
 * route, reached from `VDashboard`'s own "Finish setup" link on a `SETUP_UNFINISHED` workspace —
 * found live: a workspace that passed the scope check but not `workspaceCompleteOnboarding` had no
 * way back into this wizard at all, since `ShellLayer`'s full-screen gate only re-mounts this layer
 * for an org with ZERO workspaces, and the in-shell `/onboarding` route it already had never took a
 * workspace id, so it always restarted stage 1 — which, for an existing workspace, dead-ends
 * `workspaceConnect` on `WORKSPACE_ALREADY_CONNECTED`). `resumeWorkspaceId` fetches that workspace
 * once and re-enters at stage 4 regardless of which of §12.6.5's last three rows is actually true
 * (`state.ts`'s own `RESUMED` case explains why landing on the scope check specifically is the safe
 * choice, not a guess). The `credential_status: 'PENDING'` row is deliberately NOT handled here —
 * that is credential recovery (§12.7), a different screen (`/credential`), not this wizard.
 *
 * **`onCompleted` fires once, the render after `state.stage` reaches `'done'`** (W153, this
 * layer's first real caller — `ShellLayer`'s own `/onboarding` route) — `workspace` is the
 * connected workspace and `started` is the `JobRef` `workspaceCompleteOnboarding` returned, or
 * `null` when the first capture was deferred rather than started now.
 *
 * **The rail is five steps, not six** — `ob-wz-5` (repository scope) is temporarily out of the
 * active flow; `state.ts`'s own doc comment has the full reasoning (a wrong repository count and a
 * leaked internal dev note, found live, plus the fact that skipping it changes nothing observable
 * since its only working option already matched the backend's own defaults).
 */
export function OnboardingLayer({
  orgId,
  resumeWorkspaceId,
  onCompleted,
}: {
  orgId: string
  resumeWorkspaceId?: string
  onCompleted: (workspace: Workspace, started: JobRef | null) => void
}) {
  const [state, dispatch] = useReducer(onboardingReducer, initialOnboardingState)

  const resume = useQuery({
    queryKey: ['onboarding-resume', orgId, resumeWorkspaceId],
    enabled: Boolean(resumeWorkspaceId),
    queryFn: async (): Promise<Workspace> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}', {
        params: { path: { org_id: orgId, workspace_id: resumeWorkspaceId! } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as Workspace
    },
  })

  useEffect(() => {
    if (resume.data && state.stage === 1) dispatch({ type: 'RESUMED', workspace: resume.data })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume.data])

  useEffect(() => {
    if (state.stage === 'done') onCompleted(state.workspace, state.started)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const stageNumber = state.stage === 'done' ? 5 : STAGE_ORDINAL[state.stage]

  // Still resolving (or failed to resolve) the resume fetch — never fall through to `ob-wz-1`'s
  // "enter a fresh credential" for a workspace that already has one.
  if (resumeWorkspaceId && state.stage === 1 && !resume.data) {
    const problem = resume.error instanceof ApiProblemError ? resume.error.problem : undefined
    return (
      <WizardMachine>
        <div className="p-10 text-center text-tx2">{problem ? <Note>{problem.title}</Note> : 'Loading…'}</div>
      </WizardMachine>
    )
  }

  return (
    <WizardMachine>
      <StepRail>
        {STEP_LABELS.map((label, i) => {
          const ordinal = i + 1
          const stepState = ordinal < stageNumber ? 'done' : ordinal === stageNumber ? 'current' : 'pending'
          return (
            <Fragment key={label}>
              <Step ordinal={ordinal} label={label} state={stepState} />
              {ordinal < STEP_LABELS.length && <span aria-hidden="true" style={STEP_BAR_STYLE} className={STEP_BAR_CLASSNAME} />}
            </Fragment>
          )
        })}
      </StepRail>

      {state.stage === 1 && (
        <ObWz1 initialCredential={state.credential} onNext={(credential) => dispatch({ type: 'CREDENTIAL_ENTERED', credential })} />
      )}
      {state.stage === 2 && (
        <ObWz2
          initialWorkspaceName={state.workspaceName}
          onBack={() => dispatch({ type: 'BACK' })}
          onNext={(workspaceName) => dispatch({ type: 'WORKSPACE_NAMED', workspaceName })}
        />
      )}
      {state.stage === 3 && (
        <ObWz3
          orgId={orgId}
          credential={state.credential}
          workspaceName={state.workspaceName}
          onBack={() => dispatch({ type: 'BACK' })}
          onConnected={(workspace) => dispatch({ type: 'CONNECTED', workspace })}
        />
      )}
      {state.stage === 4 && (
        <ObWz4
          orgId={orgId}
          workspaceId={state.workspace.workspace_id}
          workspaceName={state.workspace.name}
          onBack={() => dispatch({ type: 'BACK' })}
          onPassed={() => dispatch({ type: 'SCOPE_CHECK_PASSED' })}
        />
      )}
      {state.stage === 6 && (
        <ObWz6 orgId={orgId} workspaceId={state.workspace.workspace_id} onCompleted={(started) => dispatch({ type: 'COMPLETED', started })} />
      )}
      {state.stage === 'done' && <p className="p-8 text-center text-tx2">Onboarding complete.</p>}
    </WizardMachine>
  )
}
